/**
 * The kitchen the file describes, as the game would draw it.
 *
 * The glyph grid above it is a *file* being edited; this is what that file
 * becomes. It exists because the two are not the same picture: `A` is a letter,
 * a stove is a hob with a pan on it, and whether the pan the `// container:`
 * line places actually lands on that hob is not a thing a grid of letters can
 * show. A designer reading "would boot" still has to know what they drew.
 *
 * It is not a second renderer and not a second engine. `initialState` is the
 * function the server calls when a round starts, `serialise` is the keyframe it
 * sends, and the caller pushes the result into a real `EventFeed` under a real
 * `Stage` — the same path the tutorial sandbox already takes. So a preview
 * that looks wrong is a kitchen that *is* wrong.
 *
 * Two things the editor cannot know, and which are therefore stated here rather
 * than guessed at by the caller: the clocks. `GET /api/editor/content` sends a
 * recipe book, `plateCapacity` and only the order clocks a schedule is checked
 * against (the knob contract), and `initialState` wants a plate count. Neither
 * is visible in a still frame of an unstarted round — nothing is cooking, the
 * clock is not running — so they are named constants with a reason next to them
 * rather than numbers smuggled out of the server.
 */

import type { PlayerId, SerialisedState } from '@overcooked/shared'
import { initialState, parseMap, serialise } from '@overcooked/engine'
import type { Content, EditorContent } from '@overcooked/engine'

/**
 * How many clean plates the preview's stack shows.
 *
 * The drawing is "a stack with plates on it" at any number above zero, and the
 * shipped knob is four. A preview that showed an empty stack would read as a
 * kitchen missing its plates.
 */
export const PREVIEW_PLATES = 4

/** Ticks in the preview's round. Never counted down: nothing here ticks. */
const PREVIEW_ROUND_MS = 300_000

/** The chefs in the preview: one per spawn, so every seat is shown standing. */
const previewSeats = (count: number): PlayerId[] =>
  Array.from({ length: count }, (_, i) => `preview-${i + 1}`)

/**
 * The draft as an unstarted round, or `null` if it is not a kitchen yet.
 *
 * Null rather than a thrown error, because the common case is *mid-keystroke*:
 * a row half typed is not a map, and the page says "draw a kitchen that parses"
 * and keeps the last good picture's place rather than showing a stack trace.
 * The checklist beside it is what explains why — this function stays silent on
 * purpose, so there is exactly one place the reasons are written.
 */
export function previewState(
  text: string,
  name: string,
  base: EditorContent | null,
): SerialisedState | null {
  if (!base) return null
  try {
    const map = parseMap(text, name)
    // The same bundle `checkDraft` builds, plus the two clocks `initialState`
    // reads and `validateMap` does not.
    const content = {
      map,
      maps: { [map.name]: map },
      recipes: base.recipes,
      timings: {
        plateCapacity: base.plateCapacity,
        platesPerMap: PREVIEW_PLATES,
        roundLengthMs: PREVIEW_ROUND_MS,
      },
    } as unknown as Content
    return serialise(initialState(content, previewSeats(map.spawns.length)))
  } catch {
    return null
  }
}
